import { useMemo } from 'react'
import { Address, erc20ABI, useAccount, useContractRead } from 'wagmi'
import { useActiveChainId } from './useActiveChainId'
import XTokenABI from 'config/abi/XToken.json'
import BigNumber from 'bignumber.js'

export const useXTokenLockedAmount = (tokenAddress?: Address) => {
  const { address: account } = useAccount()
  const { chainId } = useActiveChainId()

  // First, check if the token has the `isLock` function (to identify XToken)
  const { data: isLockExists, error: isLockError } = useContractRead({
    chainId,
    abi: XTokenABI,
    address: tokenAddress,
    functionName: 'isLock',
    enabled: !!tokenAddress,
    onError: () => {
      // Silently catch errors - this just means it's not an XToken
    },
  })

  // If it's an XToken, get the locked amount
  const { data: lockedAmountData, isLoading, error: lockedError } = useContractRead({
    chainId,
    abi: XTokenABI,
    address: tokenAddress,
    functionName: 'lockedAmount',
    args: [account || '0x'],
    enabled: !!account && !!tokenAddress && isLockExists !== undefined && !isLockError,
    watch: true,
    onError: () => {
      // Silently catch errors
    },
  })

  return useMemo(() => {
    const isXToken = isLockExists !== undefined && !isLockError
    if (!isXToken || !lockedAmountData || lockedError) {
      return {
        isXToken: false,
        balance: null,
        locked: null,
        isLoading: false,
      }
    }

    try {
      // lockedAmountData is a tuple [balance, locked]
      const [balance, locked] = lockedAmountData as [bigint, bigint]

      return {
        isXToken: true,
        balance: new BigNumber(balance.toString()),
        locked: new BigNumber(locked.toString()),
        isLoading,
      }
    } catch (error) {
      // If there's any error parsing the data, treat as non-XToken
      return {
        isXToken: false,
        balance: null,
        locked: null,
        isLoading: false,
      }
    }
  }, [isLockExists, isLockError, lockedAmountData, lockedError, isLoading])
} 